// Tap-to-edit clip menu, shown next to the tapped clip: Split · Copy ·
// Copy layers · Paste · Duplicate · Move here · Range · Trim/Tools · Delete.
import React from "react";
import { Scissors, Copy, ClipboardCopy, ClipboardPaste, Layers, MoveHorizontal, BoxSelect, SlidersHorizontal, Trash2, Check, CheckSquare, Crop, Eraser } from "lucide-react";

const MENU_W = 340;

export default function ClipToolbar({
  kind, visible, anchor, rangeMode, range, onSplit, onDuplicate, onCopy, onCopyLayers, onMultiSelect, onPaste, onMoveHere, onDelete, onOpenTools, onDeselect,
  onToggleRange, onRangeSplit, onRangeTrim, onRangeDelete, onRangeClear,
}) {
  if (!visible) return null;
  const hasRange = !!range && range.endMs - range.startMs > 0;
  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const pos = anchor
    ? { top: anchor.top > 150 ? anchor.top - 8 : anchor.bottom + 8, left: Math.max(8, Math.min(vw - Math.min(MENU_W, vw - 16) - 8, anchor.left)),
        transform: anchor.top > 150 ? "translateY(-100%)" : "none", width: Math.min(MENU_W, vw - 16) }
    : null;
  return (
    <div className={pos ? "fixed z-30 rounded-xl border border-fuchsia-400/30 bg-[#0f0b1a]/95 backdrop-blur shadow-[0_8px_24px_rgba(0,0,0,0.6)]"
      : "fixed inset-x-0 bottom-[76px] z-30 border-t border-white/10 bg-[#0f0b1a]/95 backdrop-blur"}
      style={pos || undefined} data-testid="clip-toolbar" data-anchored={pos ? "1" : "0"} role="toolbar"
      aria-label={kind === "beat" ? "Beat clip actions" : "Vocal clip actions"}>
      <div className="flex flex-nowrap overflow-x-auto items-center gap-1.5 px-2 py-2" style={{ scrollbarWidth: "none" }}>
        <span className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold whitespace-nowrap mr-1">
          {kind === "beat" ? "Beat" : hasRange ? `Range ${((range.endMs - range.startMs) / 1000).toFixed(2)}s` : "Vocal"}
        </span>
        {hasRange ? (
          <>
            <TBtn onClick={onRangeSplit} testid="range-split-btn" icon={Scissors}>Split</TBtn>
            <TBtn onClick={onRangeTrim} testid="range-trim-btn" icon={Crop}>Trim</TBtn>
            {onCopyLayers && <TBtn onClick={onCopyLayers} testid="range-copy-layers-btn" icon={Layers}>Copy layers</TBtn>}
            <TBtn onClick={onRangeDelete} testid="range-delete-btn" icon={Eraser} danger>Delete</TBtn>
            <TBtn onClick={onRangeClear} testid="range-clear-btn">Clear</TBtn>
          </>
        ) : (
          <>
            {onSplit && <TBtn onClick={onSplit} testid="clip-split-btn" icon={Scissors}>Split</TBtn>}
            {kind === "vocal" && onCopy && <TBtn onClick={onCopy} testid="clip-copy-btn" icon={ClipboardCopy}>Copy</TBtn>}
            {kind === "vocal" && onCopyLayers && <TBtn onClick={onCopyLayers} testid="clip-copy-layers-btn" icon={Layers}>Copy layers</TBtn>}
            {kind === "vocal" && onMultiSelect && <TBtn onClick={onMultiSelect} testid="clip-multi-select-btn" icon={CheckSquare}>Select clips</TBtn>}
            {kind === "vocal" && onPaste && <TBtn onClick={onPaste} testid="clip-paste-btn" icon={ClipboardPaste}>Paste</TBtn>}
            {kind === "vocal" && <TBtn onClick={onDuplicate} testid="clip-duplicate-btn" icon={Copy}>Duplicate</TBtn>}
            {kind === "vocal" && onMoveHere && <TBtn onClick={onMoveHere} testid="clip-move-here-btn" icon={MoveHorizontal}>Move to playhead</TBtn>}
            {kind === "vocal" && (
              <TBtn onClick={onToggleRange} testid="clip-range-btn" icon={BoxSelect} active={rangeMode}>Range</TBtn>
            )}
            <TBtn onClick={onOpenTools} testid="clip-more-btn" icon={SlidersHorizontal}>Trim / Fade</TBtn>
            {onDelete && <TBtn onClick={onDelete} testid="clip-delete-btn" icon={Trash2} danger>Delete</TBtn>}
            <TBtn onClick={onDeselect} testid="clip-deselect-btn" icon={Check}>Done</TBtn>
          </>
        )}
      </div>
      <p className="text-[9px] text-gray-500 px-3 pb-1.5 truncate" data-testid="clip-toolbar-hint">
        {rangeMode ? "Drag across the clip to select a section"
          : "Swipe for more · drag clip to move · Copy layers grabs stacked vocals"}
      </p>
    </div>
  );
}

function TBtn({ onClick, testid, children, icon: Icon, active, danger }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold border whitespace-nowrap transition-colors ${
        active ? "border-fuchsia-400 bg-fuchsia-500/20 text-fuchsia-100"
          : danger ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
            : "border-white/15 text-gray-200 hover:border-white/30 hover:bg-white/5"}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}{children}
    </button>
  );
}
